import { useSetAtom } from "jotai";
import type { LoginEntryPoint } from "#/lib/analytics";
import { formatWalletAddress } from "#/lib/formatters";
import {
	loginDialogOpenAtom,
	loginEntryPointAtom,
} from "#/stores/login-dialog-store";
import { useAuth } from "./use-auth";

/**
 * The wallet facade — the only place the rest of the app reads wallet
 * identity from. The wallet is server-custodied: its address arrives with the
 * session (`/v1/auth/email/verify`), so connected === authenticated and
 * there's no async wallet-readiness state (`isReady` is kept for API
 * stability; the session loads synchronously from localStorage).
 */
export function useWallet() {
	const { walletAddress, email, isAuthenticated } = useAuth();
	const openLoginDialog = useSetAtom(loginDialogOpenAtom);
	const setLoginEntryPoint = useSetAtom(loginEntryPointAtom);

	const address = walletAddress;

	// entryPoint is REQUIRED so every opener attributes its surface — the
	// analytics funnel splits `login_started` by it. `?? "unknown"` is a runtime
	// net for stripped-type JS callers; TS callers must pass an explicit value.
	const connect = (entryPoint: LoginEntryPoint) => {
		setLoginEntryPoint(entryPoint ?? "unknown");
		openLoginDialog(true);
	};

	return {
		address,
		displayAddress: address ? formatWalletAddress(address) : null,
		email,
		isConnected: address !== null,
		isReady: true,
		isAuthenticated,
		connect,
	};
}
